import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import { Constants } from 'src/shared/utilities/constants.util';
import { EntityAdminConfig } from 'src/shared/Baseclasses/entity-admin.component';
import { PageLayoutService } from 'src/shared/services/page-layout.service';
import { Role } from 'src/shared/models/role.model';
import { User } from 'src/shared/models/user.model';
import { UserService } from 'src/shared/services/user.service';
import { ClientService } from 'src/shared/services/client.service';
import { Client } from 'src/shared/models/client.model';
import { HsgUserProfile, HsgUserService } from '@hhsg/hsg-asc';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'smt-user',
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.scss'],
    providers: [PageLayoutService]
})
export class UserComponent extends EntityAdminConfig implements OnDestroy, OnInit {
    @ViewChild('userEditForm') public userEditForm: NgForm;

    allRoles: Role[] = [];
    enableEmail = false;
    enableSelectedRole = true;
    roles: Role[] = [];
    selectedEmail: string;
    selectedFirstName: string;
    selectedLastName: string;
    selectedRoleId: number;
    users: User[] = [];
    isShiftLeaderRoleSelected: boolean;
    selectedShiftLeaderReportAccess: boolean;    
    currentClientConfiguration: Client;
    duplicateEmailError = false;
    emailErrorMessage = "";
    isAdding = false;
    private _unsubscribe$ = new Subject();

    constructor(private _userService: UserService, private _pageLayout: PageLayoutService, private _clientService: ClientService,private _hsgUserService: HsgUserService, 
    ) {
        super(_pageLayout);
    }

    ngOnDestroy() {
        super.cleanUp();
    }

    async ngOnInit() {
        super.initialize(
            Constants.Users,
            ["First Name", "Last Name", "Email", "Last Modified"],
            async () => this.setSelectedUserValues(),
            async (mostRecent: boolean) => this.refreshUserList(mostRecent),
            async () => this.appendUserList(),
            async () => this.saveUser(),
            () => this.setCurrentSelectionLabel()); 
            this.checkUserRestriction();                  
    }

    async appendUserList(): Promise<void> {
        let result = await this._userService.getUsers(
            false,
            this.offset,
            this.itemsPerPage,
            this.searchText,
            this.selectedFilter,
            this.getSortOrderString());

        this.users = this.users.concat(result);
    }

    getRoleName(applicationRoleId: number) {
        let role = this.allRoles.find(a => a.id == applicationRoleId);

        if (role) {
            return role.name;
        }

        return "";
    }

    onAddUserClick() {
        this.selection = {
            applicationRoleId: 0,
            firstName: '',
            id: 0,
            lastName: '',
            email: '',
        };
        
        this.isAdding = true
        this.duplicateEmailError = false;
        this.isShiftLeaderRoleSelected = false;        
        this.showAdminActionWindow = true;
        this.enableEmail = true;
        this.setReportAccessByClientConfig();  
        super.limitAdminActionLabel();
        this.setSelectedUserValues();
    }

    onCancel() {
        super.onCancel();
        this.setSelectedUserValues();
        this.resetForm();
    }

    onSelection(user: User) {
        this.enableEmail = false;
        this.isAdding = false;
        this.duplicateEmailError = false;
        this.resetForm();
        super.onSelection(user);
    }

    async refreshUserList(mostRecent: boolean): Promise<User[]> {
        let result = await this._userService.getUsers(
            mostRecent,
            this.offset,
            this.itemsPerPage,
            this.searchText,
            this.selectedFilter,
            this.getSortOrderString());

        this.users = result;

        this.setSelectedUserValues();
        this.resetForm();

        return result;
    }

    resetForm() {
        if (!this.userEditForm) {
            return;
        }

        Object.keys(this.userEditForm.controls).forEach(control => {
            this.userEditForm.controls[control].markAsPristine();
        });
    }

    async saveUser() {
        const emailExists = await this.CheckEmailExists(this.selectedEmail);
        if(!this.selection)
        {
            return;
        }
        if (!emailExists) {
        this.enableEmail = false;
        let mostRecent = this.selection.id == 0;

        let selectedUser = new User;                
        selectedUser.id = this.selection.id;
        selectedUser.applicationRoleId = this.isShiftLeader(this.selectedRoleId)? this.getValidRoleForShiftLeader(this.selectedRoleId): this.selectedRoleId;
        selectedUser.email = this.selectedEmail;
        selectedUser.firstName = this.selectedFirstName;
        selectedUser.lastName = this.selectedLastName;

        let result = await this._userService.setUser(selectedUser);
        
        this.selection.id = result;
        this.selection.applicationRoleId = selectedUser.applicationRoleId;
        this.selection.email = selectedUser.email;
        this.selection.firstName = selectedUser.firstName;
        this.selection.lastName = selectedUser.lastName;
        this.isAdding = false;
        this.duplicateEmailError = false;

        await super.refreshAdminList(mostRecent, true);

        if (result > 0) {        
              this.resetForm();           
        }
        else {
            super.setErrorMessage("User failed to save.");
        }
        }
    }

    setCurrentSelectionLabel(): string {
        if (!this.selection) {
            return "";
        }

        if (this.selection.id == 0) {
            return "New User";
        }

        return this.selection.firstName + " " + this.selection.lastName;
    }

    async setRoles() {
        let manageableRoles = sessionStorage.getItem(Constants.ManageableRoles);
        this.allRoles = await this._userService.getRoles();        
        if (manageableRoles) {
            let validRoles = manageableRoles.split(',');            
            if (validRoles && validRoles.length > 0) {
                this.roles = this.allRoles.filter(r => validRoles.some(v => v.toLowerCase() == r.name.toLowerCase().replace(" ", "")))
            }
        }
    }

    async setRoleEnabledStatus() {
        if (!this.roles || this.roles.length == 0) {
            await this.setRoles();
        }

        if (this.selection && this.roles && this.roles.length > 0) {
            let currentRoleId = this.isShiftLeader(this.selection.applicationRoleId)? this.getRoleId(Constants.ShiftLeaderWithReportAccess) : this.selection.applicationRoleId;

            this.enableSelectedRole = !currentRoleId || currentRoleId == 0 || this.roles.some(r => currentRoleId == r.id);
        }
        else {
            this.enableSelectedRole = false;
        }
    }

    setSelectedUserValues() {
        super.setErrorMessage("");                 
        this.setRoleEnabledStatus();
        this.isShiftLeaderRoleSelected = false;        
        if (this.selection) {
            this.selectedEmail = this.selection.email;
            this.selectedFirstName = this.selection.firstName;
            this.selectedLastName = this.selection.lastName;    
            this.setSelectedUserRoleValues(this.selection.applicationRoleId);        
        }
        else {
            this.selectedEmail = "";
            this.selectedFirstName = "";
            this.selectedLastName = "";
            this.selectedRoleId = 0;                      
        }
    }

    onRoleChange() {
        if (this.isShiftLeader(this.selectedRoleId)){
            this.isShiftLeaderRoleSelected = true;
            this.setReportAccessByClientConfig();
        }                    
        else
            this.isShiftLeaderRoleSelected = false;
    }
    setSelectedUserRoleValues(applicationRoleId: any){        
        if (this.isShiftLeader(applicationRoleId)){
            this.selectedRoleId = this.getRoleId(Constants.ShiftLeaderWithReportAccess);            
            this.isShiftLeaderRoleSelected = true;
            this.selectedShiftLeaderReportAccess = this.isReportAccessEnabledForShiftLeader(applicationRoleId);
        }  
        else
            this.selectedRoleId = applicationRoleId;              
    }

    isShiftLeader(applicationRoleId: any) {
        let applicationRoleName = this.getRoleName(applicationRoleId);
        return applicationRoleName == Constants.ShiftLeaderWithReportAccess || applicationRoleName == Constants.ShiftLeaderWithoutReportAccess;
    }

    isReportAccessEnabledForShiftLeader(applicationRoleId: any) {                                
        return applicationRoleId == this.getRoleId(Constants.ShiftLeaderWithReportAccess);;
    }

    async getClientById(): Promise<Client> {
        return this._clientService.GetClientById();    
    }   

    getRoleId(roleName: string){        
        return this.allRoles.find(a => a.name == roleName).id;
    }

    getValidRoleForShiftLeader(applicationRoleId: any){        
        return !this.selectedShiftLeaderReportAccess? this.getRoleId(Constants.ShiftLeaderWithoutReportAccess): applicationRoleId;
    }

    setReportAccessByClientConfig(){
        this.getClientById().then(client => {             
            if(client){                 
                this.selectedShiftLeaderReportAccess = client.defaultReportAccesstoShiftLeaders;
            } 
        }); 
    }

    async onEmailExist(email: string) {
        let emailvalue = email.trim();
        if (this.isAdding == true)
        {
            (await this._userService.checkEmailExist(emailvalue)).subscribe((Existsemail) => {
                if (Existsemail.status == false) {
                    this.duplicateEmailError = true;
                    this.emailErrorMessage = Existsemail.message;
                }
            
                else {
                    this.duplicateEmailError = false;
                    this.emailErrorMessage = Existsemail.message;
                }
            })
        }
    }
    
    async CheckEmailExists(email:string):Promise<boolean>
    {
    const emailValue = email.trim();

    if (this.isAdding === true) {
        return new Promise(async (resolve, reject) => {
            (await this._userService.checkEmailExist(emailValue)).subscribe(
                (Existsemail) => {
                    if (Existsemail.status === false) {
                        this.duplicateEmailError = true;
                        this.emailErrorMessage = Existsemail.message;
                        resolve(true);
                    } else {
                        this.duplicateEmailError = false;
                        this.emailErrorMessage = Existsemail.message;
                        resolve(false);
                    }
                },
                (error) => {
                    console.error('Error checking email existence:', error);
                    this.duplicateEmailError = true;
                    this.emailErrorMessage = 'Error verifying email.';
                    reject(error);
                }
            );
        });
    }

    return Promise.resolve(false);
}

async checkUserRestriction()
{
    this._hsgUserService.getUserProfile()
    .pipe(takeUntil(this._unsubscribe$))
    .subscribe((userProfile: HsgUserProfile) => {
        if(userProfile.hasDataRestrictions)
        {
           this.addButtonEnabled = false;
           this.saveButtonEnabled = false; 
        }
    })
    
}
}
